'use client';

import * as React from 'react';
import {Avatar, AvatarFallback} from '@purpose/ui/components/avatar/avatar';
import {Typography} from '@purpose/ui/components/typography/typography';
import {Button} from '@purpose/ui/components/button/button';
import useTheme from '@purpose/ui/hooks/useTheme';
import {cn} from '@purpose/ui/lib/utils';
import DefaultAvatar from '../../assets/icons/AvatarFallback.svg';
import Image from 'next/image';

interface NavigationItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  hasYellowDot?: boolean;
}

interface SidebarNavigationProps {
  logo?: string;
  name: string;
  onNavigate?: (path: string) => void;
  userInfoWithLogout: React.ReactNode;
  languagePicker?: React.ReactNode;
  currentPath: string;
  sx?: Record<string, any>;
  onTopAvatarClick?: () => void;
  items: NavigationItem[];
}

const NavigationButton = ({
  icon,
  label,
  isActive,
  onClick,
  hasYellowDot
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick?: () => void;
  hasYellowDot?: boolean;
}) => {
  const theme = useTheme();

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex min-h-[60px] w-[97px] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg py-3 transition-colors',
        isActive ? 'bg-primary-base' : 'hover:bg-primary-base bg-transparent'
      )}
    >
      <div className="text-secondary-20 relative flex h-6 items-center justify-center">
        {icon}
        {hasYellowDot && (
          <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-yellow-500" />
        )}
      </div>
      <Typography
        variant="body2"
        className="text-secondary-20 text-center leading-tight break-words"
      >
        {label}
      </Typography>
    </div>
  );
};

const BaseSidebar = ({
  logo,
  name,
  items,
  onNavigate,
  languagePicker,
  userInfoWithLogout,
  currentPath,
  sx = {},
  onTopAvatarClick
}: SidebarNavigationProps) => {
  const theme = useTheme();

  return (
    <div
      className="bg-primary-110 flex h-full w-[110px] flex-col items-center justify-between px-2 py-6"
      style={sx}
    >
      <div className="flex h-[95vh] w-[110px] flex-col items-center justify-between">
        <div className="flex flex-col items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onTopAvatarClick}
            className={cn(
              'hover:bg-primary-base h-auto p-0',
              onTopAvatarClick ? 'cursor-pointer' : 'cursor-default'
            )}
            disabled={!onTopAvatarClick}
            aria-label="Avatar"
          >
            <Avatar className="h-12 w-12">
              {logo ? (
                <img
                  src={logo}
                  alt={name || 'Logo'}
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                <AvatarFallback>
                  <Image
                    src={DefaultAvatar}
                    width={48}
                    height={48}
                    alt="logo"
                  />
                </AvatarFallback>
              )}
            </Avatar>
          </Button>
          <Typography
            variant="body1"
            className="text-secondary-20 mx-1 mt-2 text-center break-all opacity-80"
          >
            {name}
          </Typography>
        </div>

        <div className="flex w-full flex-grow-0 flex-col items-center gap-2">
          {items.map(item => (
            <NavigationButton
              key={item.path}
              icon={item.icon}
              label={item.label}
              hasYellowDot={item.hasYellowDot}
              isActive={currentPath === item.path}
              onClick={() => onNavigate?.(item.path)}
            />
          ))}
        </div>

        <div className="flex flex-col items-center gap-4">
          {languagePicker}
          {userInfoWithLogout}
        </div>
      </div>
    </div>
  );
};

const AdminSidebar = (props: Omit<SidebarNavigationProps, 'items'>) => {
  const items: NavigationItem[] = [
    {
      icon: <div className="h-4 w-4">📊</div>, // BarChart2 icon placeholder
      label: 'Overview',
      path: '/overview'
    },
    {
      icon: <div className="h-4 w-4">👥</div>, // Users icon placeholder
      label: 'Management',
      path: '/management'
    },
    {
      icon: <div className="h-4 w-4">🏷️</div>, // Tag icon placeholder
      label: 'Benefits',
      path: '/benefits'
    },
    {
      icon: <div className="h-4 w-4">💰</div>, // Banknote icon placeholder
      label: 'Finances',
      path: '/finances'
    },
    {
      icon: <div className="h-4 w-4">📄</div>, // FileText icon placeholder
      label: 'Reports',
      path: '/reports'
    },
    {
      icon: <div className="h-4 w-4">🚪</div>, // LogOut icon placeholder
      label: 'Log Out',
      path: '/logout'
    }
  ];

  return <BaseSidebar {...props} items={items} />;
};

const SidebarNavigation = {
  Admin: AdminSidebar,
  Base: BaseSidebar
};

export {SidebarNavigation};
export type {SidebarNavigationProps, NavigationItem};
